/**
 * createSuiteSchedule (#2391) — put a suite on a schedule as two client
 * writes: save the on-screen settings as a scheduler-pool run-setting the
 * schedule owns, then create the schedule pointing at it. Per #2175 each
 * schedule keeps its OWN settings copy, so editing a hand-run never changes
 * what runs overnight.
 *
 * If the schedule write fails, the run-setting just created is deleted so it
 * is not left orphaned; a failed cleanup is benign — an unused scheduler-pool
 * run-setting never runs on its own (nothing points at it). Split out of
 * ReplayTemplateDialog so this two-write-plus-compensate flow can be
 * unit-tested directly.
 */
import { replayTemplatesAPI, scheduledRunsAPI } from '@/lib/api'
import type { SchedulePayload } from '@/components/ScheduleFields'
import type { RunTemplateSettings } from '@/types'

export async function createSuiteSchedule(params: {
  variantId: string
  settings: RunTemplateSettings
  schedule: SchedulePayload
}): Promise<void> {
  const { variantId, settings, schedule } = params
  const created = await replayTemplatesAPI.create({ variantId, ...settings, pool: 'scheduler' }) as { id: string }
  try {
    await scheduledRunsAPI.create({
      variantId,
      templateId: created.id,
      templateName: settings.name,
      ...schedule,
    })
  } catch (err) {
    await replayTemplatesAPI.delete(created.id).catch(() => {})
    throw err
  }
}

/**
 * updateSuiteSchedule (#2391) — edit an existing schedule from Runs: write the
 * on-screen settings back to the run-setting the schedule owns, then update the
 * schedule's timetable. Both are the operator's edits to one schedule, so no
 * compensating delete: if the timetable write fails the settings still saved,
 * and the operator sees the error and can retry.
 */
export async function updateSuiteSchedule(params: {
  scheduleId: string
  templateId: string
  variantId: string
  settings: RunTemplateSettings
  schedule: SchedulePayload
}): Promise<void> {
  const { scheduleId, templateId, variantId, settings, schedule } = params
  // The run-setting's variant is fixed; update only its settings.
  await replayTemplatesAPI.update(templateId, settings)
  await scheduledRunsAPI.update(scheduleId, {
    variantId,
    templateId,
    templateName: settings.name,
    ...schedule,
  })
}
